/**
 * benefitsView.js — PROD-581 (insurance level 1). ONE READER that turns the eligibility answer
 * Orella already stores (profile.insurance.last_eligibility / last_eligibility_secondary, the
 * parsed Stedi 271 written by orella-api rcm_service) into what the "My insurance" screen shows.
 * Level 1 uses ONLY that stored answer: no new request, no new field the API must send.
 *
 * Alex 2026-10-05 (plans/ANSWERS.md, V-1..V-9), as rules here:
 *   V-3  no age warning: the view carries the check's date and nothing else about freshness.
 *   V-5  in-network individual figures up front; family figures apart (the screen's expander).
 *   V-6  a figure the payer did not report is null ("Not reported by your insurer"), NEVER 0 and
 *        NEVER another figure standing in (the office copay is not a video-visit copay).
 *   V-7  no totals: nothing here adds figures up. The only arithmetic is met = total - remaining
 *        inside one bucket (utils/coverageLevels.js), which the existing card already shows.
 *   V-2  the patient's Re-check is offered once per 30 days (recheckAvailableAt).
 *
 * NETWORK, FEATURE-DETECTED (PROD-587). orella-api from the ins1 build on stores
 * last_eligibility.benefitNetworks {figure key: 'in' | 'out' | 'not_stated'} and
 * benefits[].copayNetwork / coinsuranceNetwork. A result without them (every result stored by
 * the live API v243.292.0) has network null on copays and coinsurance: the screen then names NO
 * network for them, rather than implying in-network. `inNetwork` (the first line's flag) is
 * never read. Deductible / out-of-pocket figures carry the network of their spendDown bucket
 * ('unknown' -> 'not_stated').
 *
 * Pure apart from coverageLevels.js (pure too). Gate: scripts/check-insurance-level1.mjs IL-581.
 */
import { spendAtLevel } from './coverageLevels'

// PROD-936 PAT-2 (Alex 2026-10-13, decision 2): 30 days, the server's PATIENT_ELIGIBILITY_REUSE_DAYS
//   default (was 7 days, V-2). Inside it the server answers the stored result with no payer call.
export const RECHECK_INTERVAL_MS = 30 * 24 * 3600 * 1000

const NETS = new Set(['in', 'out', 'not_stated'])
const net = (v) => (NETS.has(v) ? v : null)
const cents = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null)
const ACTIVE = new Set(['active'])
const NEGATIVE = new Set(['inactive', 'not_found', 'data_mismatch'])
const TELEHEALTH_RE = /tele|virtual|video/i

function resultClassOf(e) {
  const rc = String(e?.resultClass || '').toLowerCase()
  if (rc) return rc
  const s = String(e?.status || '').toLowerCase()
  if (s === 'active' || s === 'verified') return 'active'
  if (s === 'inactive') return 'inactive'
  return e?.checkedAt ? 'unknown' : null
}

function rowFor(benefits, stc) {
  return benefits.find((b) => b && String(b.serviceTypeCode || '') === stc && cents(b.copayCents) !== null) || null
}

function spend(e, kind) {
  const one = (level) => {
    const s = spendAtLevel(e, kind, level)
    if (!s) return null
    return { level, network: s.network === 'unknown' ? 'not_stated' : net(s.network), total: cents(s.total), remaining: cents(s.remaining) }
  }
  return { individual: one('individual'), family: one('family') }
}

/**
 * The screen's view of ONE coverage's stored answer.
 *   state  'none' (nothing checked) | 'active' | 'negative' (inactive / not_found /
 *          data_mismatch: the API's own headline and message, no numbers) | 'other' (a check that
 *          did not reach an answer: payer unreachable, setting up, ...; no numbers)
 */
export function buildBenefitsView(eligibility) {
  const e = eligibility && typeof eligibility === 'object' ? eligibility : null
  const rc = e ? resultClassOf(e) : null
  const base = { state: 'none', resultClass: rc, checkedAt: e && typeof e.checkedAt === 'string' ? e.checkedAt : null }
  if (!e || !rc) return base
  if (NEGATIVE.has(rc)) return { ...base, state: 'negative' }
  if (!ACTIVE.has(rc)) return { ...base, state: 'other' }

  const nets = e.benefitNetworks && typeof e.benefitNetworks === 'object' ? e.benefitNetworks : {}
  const benefits = Array.isArray(e.benefits) ? e.benefits.filter((b) => b && typeof b === 'object') : []
  const fromRow = (row) => (row ? { cents: cents(row.copayCents), network: net(row.copayNetwork) } : { cents: null, network: null })
  const tele = benefits.find((b) => TELEHEALTH_RE.test(String(b.serviceType || '')) && cents(b.copayCents) !== null) || null

  const perVisit = [
    { id: 'specialist', cents: cents(e.copaySpecialistCents), network: net(nets.copaySpecialistCents) },
    { id: 'primaryCare', cents: cents(e.copayPcpCents), network: net(nets.copayPcpCents) },
    { id: 'urgentCare', ...fromRow(rowFor(benefits, 'UC')) },
    { id: 'emergency', ...fromRow(rowFor(benefits, '86')) },
    { id: 'videoVisit', ...fromRow(tele) },
  ]
  const shown = new Set(['30', '98', '1', 'BZ', 'UC', '86'])
  const otherServices = benefits
    .filter((b) => b !== tele && !shown.has(String(b.serviceTypeCode || '')) && cents(b.copayCents) !== null && String(b.serviceType || '').trim())
    .map((b) => ({ label: String(b.serviceType).trim(), cents: cents(b.copayCents), network: net(b.copayNetwork) }))
  const pct = typeof e.coinsurancePercent === 'number' && Number.isFinite(e.coinsurancePercent) ? e.coinsurancePercent : null
  const planType = typeof e.planType === 'string' && e.planType ? e.planType.toUpperCase() : null

  return {
    ...base,
    state: 'active',
    planName: e.planIdentifiers?.planName || null,
    planType,
    perVisit,
    deductible: spend(e, 'deductible'),
    outOfPocket: spend(e, 'outOfPocket'),
    coinsurance: pct === null ? null : { pct, network: net(nets.coinsurancePercent) },
    // derived from the plan type (rcm_service does the same), so it is worded as "usually"
    referralLikely: planType === 'HMO' || planType === 'POS' ? planType : null,
    otherServices,
  }
}

/** V-2: when the patient's Re-check is offered again (ISO), or null when it is offered now. */
export function recheckAvailableAt(checkedAt, now = Date.now()) {
  const t = Date.parse(checkedAt || '')
  if (!Number.isFinite(t)) return null
  const at = t + RECHECK_INTERVAL_MS
  return at > now ? new Date(at).toISOString() : null
}

/** The check's date in the UI language's locale ('' when there is none). */
export function benefitsDate(iso, language) {
  const d = new Date(iso || '')
  if (!iso || !Number.isFinite(d.getTime())) return ''
  return d.toLocaleDateString(language === 'es' ? 'es-US' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
